import { fbt } from 'fbtee'
import { Check } from 'lucide-react'
import { pregameGoingIds } from '@shared/pregames'
import { MemberFacepile } from './MemberFacepile'
import { pregameRsvpOf, type PregameRsvp } from '../hooks/usePregames'
import { cn } from '../lib/utils'
import type { Pregame, UserProfile } from '../types'

const CHIP = 'flex-none cursor-pointer rounded-full border px-3 py-1 text-xs font-semibold whitespace-nowrap transition-colors'
const toggle = (answer: PregameRsvp | null, status: PregameRsvp) => (answer === status ? null : status)

/** Who's going to a pre-game, and — for an invitee — their going / not going answer. */
export function PregameRsvpRow({ pregame, currentUserId, profiles, onRsvp }: {
  pregame: Pregame
  currentUserId: string | null
  profiles: Record<string, UserProfile>
  /** Invitees only; the host doesn't answer their own pre-game. Null takes the answer back. */
  onRsvp?: (status: PregameRsvp | null) => void
}) {
  const going = pregameGoingIds(pregame)
  const answer = pregameRsvpOf(pregame, currentUserId)

  return (
    <div className="mt-3 flex items-center gap-2 border-t border-border pt-3" data-testid="pregame-rsvp">
      <MemberFacepile
        interestedMemberIds={[]}
        goingMemberIds={going}
        profiles={profiles}
        currentUserId={currentUserId}
        dialogTitle={fbt('Going to the pre-game', 'Title of the list of people going to a pre-game')}
        historyKey={`pregame:${pregame.id}`}
        size="sm"
        max={4}
      />
      <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
        {fbt(fbt.plural('going', going.length, { showCount: 'yes', many: 'going' }), 'How many people are going to a pre-game, next to their faces')}
      </span>
      {onRsvp && (
        <>
          <button
            type="button"
            // Unanswered it's the call to action (blue, like the banner's); going turns it green.
            className={cn(
              CHIP,
              answer === 'going' ? 'border-going bg-going text-going-foreground'
                : answer === 'not_going' ? 'border-transparent text-muted-foreground hover:text-foreground'
                  : 'border-primary bg-primary text-primary-foreground hover:bg-primary/90',
            )}
            onClick={() => onRsvp(toggle(answer, 'going'))}
            aria-pressed={answer === 'going'}
          >
            {answer === 'going' && <Check className="mr-1 inline size-3" />}
            {fbt("I'm going", 'Button an invitee taps to say they are going to a pre-game')}
          </button>
          <button
            type="button"
            // Unanswered it's a plain text button, like the banner's; picked, it gets a border.
            className={cn(CHIP, answer === 'not_going' ? 'border-muted-foreground/50 bg-muted text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}
            onClick={() => onRsvp(toggle(answer, 'not_going'))}
            aria-pressed={answer === 'not_going'}
          >
            {answer === 'not_going' && <Check className="mr-1 inline size-3" />}
            {fbt("Can't go",'Button an invitee taps to say they are not going to a pre-game')}
          </button>
        </>
      )}
    </div>
  )
}
